import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Input — Volume 03 §2. Heights sm/md/lg = 36/44/52.
 * Trạng thái lỗi đổi border + icon + text (không chỉ đổi màu — Volume 02 §8).
 */
const inputVariants = cva(
  "w-full rounded-md border bg-surface text-foreground " +
    "placeholder:text-disabled " +
    "transition-colors duration-fast ease-out " +
    "focus-visible:outline-none focus-visible:ring focus-visible:ring-ring focus-visible:ring-offset-0 focus-visible:border-primary " +
    "disabled:cursor-not-allowed disabled:bg-subtle disabled:text-disabled read-only:bg-subtle",
  {
    variants: {
      inputSize: {
        sm: "h-9 px-3 text-body-sm",
        md: "h-11 px-3.5 text-body",
        lg: "h-[52px] px-4 text-body",
      },
      invalid: {
        true: "border-danger focus-visible:border-danger focus-visible:ring-danger/40",
        false: "border-border-strong",
      },
    },
    defaultVariants: {
      inputSize: "md",
      invalid: false,
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, inputSize, invalid, "aria-invalid": ariaInvalid, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={ariaInvalid ?? (invalid ? true : undefined)}
      className={cn(inputVariants({ inputSize, invalid }), className)}
      {...props}
    />
  )
);
Input.displayName = "Input";

/** Helper hoặc error text đặt ngay dưới field (Volume 02 §13.2). */
export function FieldHint({
  error,
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & { error?: boolean }) {
  return (
    <p
      className={cn("mt-1.5 text-body-sm", error ? "text-danger" : "text-muted", className)}
      {...props}
    >
      {children}
    </p>
  );
}
